/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import * as coreHttp from "@azure/core-http";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { SearchServiceClient } from "../searchServiceClient";
import {
  SearchIndexerSkillset,
  SkillsetsCreateOrUpdateOptionalParams,
  SkillsetsCreateOrUpdateResponse,
  SkillsetsDeleteOptionalParams,
  SkillsetsGetOptionalParams,
  SkillsetsGetResponse,
  SkillsetsListOptionalParams,
  SkillsetsListResponse,
  SkillsetsCreateOptionalParams,
  SkillsetsCreateResponse
} from "../models";

/** Class representing a Skillsets. */
export class Skillsets {
  private readonly client: SearchServiceClient;

  /**
   * Initialize a new instance of the class Skillsets class.
   * @param client Reference to the service client
   */
  constructor(client: SearchServiceClient) {
    this.client = client;
  }

  /**
   * Creates a new skillset in a search service or updates the skillset if it already exists.
   * @param skillsetName The name of the skillset to create or update.
   * @param skillset The skillset containing one or more skills to create or update in a search service.
   * @param options The options parameters.
   */
  createOrUpdate(
    skillsetName: string,
    skillset: SearchIndexerSkillset,
    options?: SkillsetsCreateOrUpdateOptionalParams
  ): Promise<SkillsetsCreateOrUpdateResponse> {
    const operationArguments: coreHttp.OperationArguments = {
      skillsetName,
      skillset,
      options: coreHttp.operationOptionsToRequestOptionsBase(options || {})
    };
    return this.client.sendOperationRequest(
      operationArguments,
      createOrUpdateOperationSpec
    ) as Promise<SkillsetsCreateOrUpdateResponse>;
  }

  /**
   * Deletes a skillset in a search service.
   * @param skillsetName The name of the skillset to delete.
   * @param options The options parameters.
   */
  delete(
    skillsetName: string,
    options?: SkillsetsDeleteOptionalParams
  ): Promise<coreHttp.RestResponse> {
    const operationArguments: coreHttp.OperationArguments = {
      skillsetName,
      options: coreHttp.operationOptionsToRequestOptionsBase(options || {})
    };
    return this.client.sendOperationRequest(
      operationArguments,
      deleteOperationSpec
    ) as Promise<coreHttp.RestResponse>;
  }

  /**
   * Retrieves a skillset in a search service.
   * @param skillsetName The name of the skillset to retrieve.
   * @param options The options parameters.
   */
  get(
    skillsetName: string,
    options?: SkillsetsGetOptionalParams
  ): Promise<SkillsetsGetResponse> {
    const operationArguments: coreHttp.OperationArguments = {
      skillsetName,
      options: coreHttp.operationOptionsToRequestOptionsBase(options || {})
    };
    return this.client.sendOperationRequest(
      operationArguments,
      getOperationSpec
    ) as Promise<SkillsetsGetResponse>;
  }

  /**
   * List all skillsets in a search service.
   * @param options The options parameters.
   */
  list(options?: SkillsetsListOptionalParams): Promise<SkillsetsListResponse> {
    const operationArguments: coreHttp.OperationArguments = {
      options: coreHttp.operationOptionsToRequestOptionsBase(options || {})
    };
    return this.client.sendOperationRequest(
      operationArguments,
      listOperationSpec
    ) as Promise<SkillsetsListResponse>;
  }

  /**
   * Creates a new skillset in a search service.
   * @param skillset The skillset containing one or more skills to create in a search service.
   * @param options The options parameters.
   */
  create(
    skillset: SearchIndexerSkillset,
    options?: SkillsetsCreateOptionalParams
  ): Promise<SkillsetsCreateResponse> {
    const operationArguments: coreHttp.OperationArguments = {
      skillset,
      options: coreHttp.operationOptionsToRequestOptionsBase(options || {})
    };
    return this.client.sendOperationRequest(
      operationArguments,
      createOperationSpec
    ) as Promise<SkillsetsCreateResponse>;
  }
}
// Operation Specifications
const serializer = new coreHttp.Serializer(Mappers, /* isXml */ false);

const createOrUpdateOperationSpec: coreHttp.OperationSpec = {
  path: "/skillsets('{skillsetName}')",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.SearchIndexerSkillset
    },
    201: {
      bodyMapper: Mappers.SearchIndexerSkillset
    },
    default: {
      bodyMapper: Mappers.SearchError
    }
  },
  requestBody: Parameters.skillset,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.skillsetName],
  headerParameters: [
    Parameters.contentType,
    Parameters.accept,
    Parameters.xMsClientRequestId,
    Parameters.ifMatch,
    Parameters.ifNoneMatch,
    Parameters.prefer
  ],
  mediaType: "json",
  serializer
};
const deleteOperationSpec: coreHttp.OperationSpec = {
  path: "/skillsets('{skillsetName}')",
  httpMethod: "DELETE",
  responses: {
    204: {},
    404: {},
    default: {
      bodyMapper: Mappers.SearchError
    }
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.skillsetName],
  headerParameters: [
    Parameters.accept,
    Parameters.xMsClientRequestId,
    Parameters.ifMatch,
    Parameters.ifNoneMatch
  ],
  serializer
};
const getOperationSpec: coreHttp.OperationSpec = {
  path: "/skillsets('{skillsetName}')",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SearchIndexerSkillset
    },
    default: {
      bodyMapper: Mappers.SearchError
    }
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.skillsetName],
  headerParameters: [Parameters.accept, Parameters.xMsClientRequestId],
  serializer
};
const listOperationSpec: coreHttp.OperationSpec = {
  path: "/skillsets",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ListSkillsetsResult
    },
    default: {
      bodyMapper: Mappers.SearchError
    }
  },
  queryParameters: [Parameters.apiVersion, Parameters.select],
  urlParameters: [Parameters.endpoint],
  headerParameters: [Parameters.accept, Parameters.xMsClientRequestId],
  serializer
};
const createOperationSpec: coreHttp.OperationSpec = {
  path: "/skillsets",
  httpMethod: "POST",
  responses: {
    201: {
      bodyMapper: Mappers.SearchIndexerSkillset
    },
    default: {
      bodyMapper: Mappers.SearchError
    }
  },
  requestBody: Parameters.skillset,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint],
  headerParameters: [
    Parameters.contentType,
    Parameters.accept,
    Parameters.xMsClientRequestId
  ],
  mediaType: "json",
  serializer
};
